import { useMutation, useQuery } from '@tanstack/react-query';
import { authApi } from '@/api/auth.api';
import { useAuth } from '@/auth/useAuth';
import { qk } from '@/config/queryKeys';
import type { ForgotPasswordRequest, LoginRequest } from '@/types/auth';

// React Query hooks for the Authentication module — thin, component-friendly wrappers over
// the auth service + session store. Refresh-token rotation is automatic in apiClient.

/** POST /api/auth/login (then GET /api/auth/profile) via the session store. */
export function useLogin() {
  const { login } = useAuth();
  return useMutation({ mutationFn: (req: LoginRequest) => login(req) });
}

/** POST /api/auth/logout via the session store (revokes the refresh token). */
export function useLogout() {
  const { logout } = useAuth();
  return useMutation({ mutationFn: () => logout() });
}

/** POST /api/auth/forgot-password (always succeeds — no account enumeration). */
export function useForgotPassword() {
  return useMutation({ mutationFn: (body: ForgotPasswordRequest) => authApi.forgotPassword(body) });
}

/** GET /api/auth/profile — current tenant user (roles + permissions). */
export function useProfile() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: qk.auth.profile,
    queryFn: authApi.profile,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}
